import { useMemo, useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import PageHeader from "@/components/common/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import EmptyState from "@/components/common/EmptyState";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  UploadCloud,
  ExternalLink,
  Loader2,
  Image as ImageIcon,
  Users
} from "lucide-react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { BACKEND_URL } from "@/config";
import { ICON_OPTIONS } from "@/constants/icons";

const blank = {
  organizationName: "",
  slug: "",
  role: "",
  category: "Foundation",
  shortDescription: "",
  contribution: "",
  websiteUrl: "",
  logoUrl: "",
  coverImageUrl: "",
  iconName: "fa-solid fa-users",
  buttonText: "Learn More",
  displayOrder: 0,
  isFeatured: false,
  isActive: true,
  startDate: "",
  endDate: ""
};

const CATEGORIES = ["Foundation", "Startup", "Non-Profit", "Community", "Advisory", "Corporate", "Other"];

export default function Leadership() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const fetchItems = async () => {
    try {
      const res = await api.get('/LeadershipOrganization');
      if (res.success && res.data) {
        const mapped = res.data.map(d => ({
          id: d.id,
          organizationName: d.organizationName || "",
          slug: d.slug || "",
          role: d.role || "",
          category: d.category || "Foundation",
          shortDescription: d.shortDescription || "",
          contribution: d.contribution || "",
          websiteUrl: d.websiteUrl || "",
          logoUrl: d.logoUrl || "",
          coverImageUrl: d.coverImageUrl || "",
          iconName: d.iconName || "",
          buttonText: d.buttonText || "",
          displayOrder: d.displayOrder || 0,
          isFeatured: d.isFeatured || false,
          isActive: d.isActive !== undefined ? d.isActive : true,
          startDate: d.startDate ? d.startDate.split("T")[0] : "",
          endDate: d.endDate ? d.endDate.split("T")[0] : ""
        }));
        setItems(mapped.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)));
      }
    } catch (err) {
      toast.error("Failed to load Leadership Organizations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return items;
    const q = query.toLowerCase();
    return items.filter(
      (e) =>
        e.organizationName.toLowerCase().includes(q) ||
        e.role.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
    );
  }, [items, query]);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setModalOpen(true);
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === 'logo') setUploadingLogo(true);
    else setUploadingCover(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/LeadershipOrganization/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.success && res.data) {
        if (type === 'logo') {
          setForm(prev => ({ ...prev, logoUrl: res.data }));
        } else {
          setForm(prev => ({ ...prev, coverImageUrl: res.data }));
        }
        toast.success(`${type === 'logo' ? 'Logo' : 'Cover image'} uploaded successfully.`);
      } else {
        toast.error(res.message || "Failed to upload image.");
      }
    } catch (err) {
      toast.error("An error occurred during upload.");
    } finally {
      if (type === 'logo') {
        setUploadingLogo(false);
        if (logoInputRef.current) logoInputRef.current.value = "";
      } else {
        setUploadingCover(false);
        if (coverInputRef.current) coverInputRef.current.value = "";
      }
    }
  };

  const save = async () => {
    if (!form.organizationName || !form.role || !form.slug) {
      toast.error("Organization Name, Role, and Slug are required.");
      return;
    }
    
    const payload = {
      ...form,
      startDate: form.startDate ? form.startDate : null,
      endDate: form.endDate ? form.endDate : null
    };

    try {
      if (editing) {
        const res = await api.put(`/LeadershipOrganization/${editing.id}`, payload);
        if (res.success) {
          toast.success("Organization updated.");
          fetchItems();
        }
      } else {
        const res = await api.post('/LeadershipOrganization', payload);
        if (res.success) {
          toast.success("Organization added.");
          fetchItems();
        }
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err.message || "Failed to save record.");
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await api.delete(`/LeadershipOrganization/${toDelete.id}`);
      if (res.success) {
        toast.success("Organization removed.");
        fetchItems();
      }
    } catch (err) {
      toast.error("Failed to delete record.");
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leadership & Community"
        description="Manage your roles in foundations, startups, and community initiatives."
        testId="leadership"
        action={openCreate}
        actionLabel="Add Organization"
        actionIcon={Plus}
      />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Input
            data-testid="leadership-search"
            placeholder="Search organizations, roles, categories…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 h-10 shadow-sm transition-all focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <Badge variant="secondary" className="font-mono text-[10px] w-fit px-3 py-1 bg-primary/10 text-primary">
          {filtered.length} {filtered.length === 1 ? "organization" : "organizations"}
        </Badge>
      </div>

      {filtered.length === 0 ? (
        <Card className="border-dashed border-2 bg-muted/20">
          <EmptyState
            icon={Users}
            title="No organizations found"
            description="Add your first leadership or community role."
            actionLabel="Add Organization"
            onAction={openCreate}
            testId="leadership-empty"
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          <AnimatePresence>
            {filtered.map((row) => (
              <motion.div
                key={row.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
              >
                <Card className={`group relative overflow-hidden flex flex-col h-full border transition-all duration-300 hover:shadow-lg ${!row.isActive ? 'opacity-60' : 'hover:border-primary/50'}`}>
                  {/* Cover Image Area */}
                  <div className="h-32 w-full relative bg-muted flex-shrink-0">
                    {row.coverImageUrl ? (
                      <img 
                        src={row.coverImageUrl?.startsWith('http') ? row.coverImageUrl : `${BACKEND_URL}${row.coverImageUrl}`} 
                        alt="Cover"
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-r from-primary/10 to-primary/5" />
                    )}
                    {row.isFeatured && (
                      <Badge className="absolute top-3 right-3 bg-primary/90 text-primary-foreground backdrop-blur-sm border-none shadow-sm">
                        Featured
                      </Badge>
                    )}
                    {!row.isActive && (
                      <Badge variant="destructive" className="absolute top-3 right-3 backdrop-blur-sm">
                        Hidden
                      </Badge>
                    )}
                  </div>

                  <CardContent className="p-5 pt-0 flex-1 flex flex-col relative">
                    {/* Logo */}
                    <div className="h-16 w-16 rounded-xl border-4 border-card bg-muted flex items-center justify-center -mt-8 mb-3 overflow-hidden z-10 shadow-sm">
                      {row.logoUrl ? (
                        <img 
                          src={row.logoUrl?.startsWith('http') ? row.logoUrl : `${BACKEND_URL}${row.logoUrl}`} 
                          alt="Logo"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Users className="h-7 w-7 text-muted-foreground/50" />
                      )}
                    </div>

                    <div className="mb-2">
                      <h3 className="font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors" title={row.organizationName}>
                        {row.organizationName}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm font-medium">{row.role}</span>
                        <span className="text-muted-foreground/50 text-xs">•</span>
                        <Badge variant="outline" className="text-[10px] font-mono bg-muted/30">
                          {row.category}
                        </Badge>
                      </div>
                    </div>
                    
                    {row.shortDescription && (
                      <p className="text-sm text-muted-foreground/80 line-clamp-3 mb-4 flex-1 mt-2">
                        {row.shortDescription}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between pt-4 border-t border-border/50 mt-auto">
                      <div className="flex gap-2 items-center text-xs font-mono text-muted-foreground/70">
                        <span>Order: {row.displayOrder}</span>
                        {row.websiteUrl && (
                          <a href={row.websiteUrl} target="_blank" rel="noreferrer" className="flex items-center hover:text-primary transition-colors">
                            <ExternalLink className="h-3 w-3 ml-2 mr-1" />
                            Link
                          </a>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                          onClick={() => openEdit(row)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive/70 hover:text-destructive hover:bg-destructive/10"
                          onClick={() => {
                            setToDelete(row);
                            setConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="leadership-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {editing ? "Edit Organization" : "Add Organization"}
            </DialogTitle>
            <DialogDescription>
              Add details about your leadership, advisory, or community role.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5 py-2">
            
            {/* Images Upload */}
            <div className="col-span-1 md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Cover Image Upload */}
              <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border rounded-xl bg-muted/10 hover:bg-muted/20 transition-colors relative group h-32">
                {form.coverImageUrl ? (
                  <div className="relative w-full h-full rounded-md overflow-hidden bg-muted">
                    <img src={form.coverImageUrl?.startsWith('http') ? form.coverImageUrl : `${BACKEND_URL}${form.coverImageUrl}`} alt="Cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button variant="secondary" size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}>
                        {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UploadCloud className="h-4 w-4 mr-2" />}
                        Change Cover
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <h4 className="text-sm font-semibold mb-2 flex items-center justify-center gap-2">
                      {uploadingCover ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                      Cover Image
                    </h4>
                    <Button variant="outline" size="sm" onClick={() => coverInputRef.current?.click()} disabled={uploadingCover}>
                      Browse
                    </Button>
                  </div>
                )}
                <input type="file" ref={coverInputRef} className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'cover')} />
              </div>

              {/* Logo Upload */}
              <div className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-border rounded-xl bg-muted/10 hover:bg-muted/20 transition-colors relative group h-32">
                {form.logoUrl ? (
                  <div className="relative h-20 w-20 rounded-xl overflow-hidden bg-muted border">
                    <img src={form.logoUrl?.startsWith('http') ? form.logoUrl : `${BACKEND_URL}${form.logoUrl}`} alt="Logo" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-white" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
                        {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <h4 className="text-sm font-semibold mb-2 flex items-center justify-center gap-2">
                      {uploadingLogo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                      Logo Image
                    </h4>
                    <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
                      Browse
                    </Button>
                  </div>
                )}
                <input type="file" ref={logoInputRef} className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo')} />
              </div>
            </div>

            <Field label="Organization Name" required>
              <Input
                value={form.organizationName}
                onChange={(e) => setForm({ ...form, organizationName: e.target.value })}
                placeholder="e.g. Acme Foundation"
                className="bg-background"
              />
            </Field>

            <Field label="Role" required>
              <Input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                placeholder="e.g. Founder, Advisor, Board Member"
                className="bg-background"
              />
            </Field>

            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            
            <Field label="Website URL">
              <Input
                type="url"
                value={form.websiteUrl}
                onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
                placeholder="https://..."
                className="bg-background"
              />
            </Field>

            <Field label="Start Date">
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                className="bg-background"
              />
            </Field>

            <Field label="End Date">
              <Input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                className="bg-background"
              />
            </Field>
            
            <Field label="Slug" required>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="e.g. acme-foundation"
                className="bg-background font-mono text-sm"
              />
            </Field>

            <div className="col-span-1 flex flex-col gap-4 justify-center">
              <div className="flex items-center justify-between border rounded-md p-3">
                <Label className="text-sm font-medium cursor-pointer" htmlFor="isActive">
                  Active (Visible on Site)
                </Label>
                <Switch 
                  id="isActive"
                  checked={form.isActive}
                  onCheckedChange={(checked) => setForm({ ...form, isActive: checked })}
                />
              </div>
              <div className="flex items-center justify-between border rounded-md p-3">
                <Label className="text-sm font-medium cursor-pointer" htmlFor="isFeatured">
                  Featured (Highlight)
                </Label>
                <Switch 
                  id="isFeatured"
                  checked={form.isFeatured}
                  onCheckedChange={(checked) => setForm({ ...form, isFeatured: checked })}
                />
              </div>
            </div>

            <Field label="Short Description" span={2}>
              <Textarea
                value={form.shortDescription}
                onChange={(e) => setForm({ ...form, shortDescription: e.target.value })}
                placeholder="Brief summary of the organization..."
                rows={2}
                className="bg-background resize-none"
              />
            </Field>

            <Field label="Contribution / Detail" span={2}>
              <Textarea
                value={form.contribution}
                onChange={(e) => setForm({ ...form, contribution: e.target.value })}
                placeholder="Detailed description of your contribution, vision, or impact..."
                rows={4}
                className="bg-background resize-none"
              />
            </Field>

            <Field label="Icon Name (FontAwesome)">
              <Select value={form.iconName} onValueChange={(v) => setForm({ ...form, iconName: v })}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="Select an icon" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {ICON_OPTIONS.map((icon) => (
                    <SelectItem key={icon.value} value={icon.value}>
                      <div className="flex items-center gap-2">
                        <i className={`${icon.value} w-4 text-center text-sm`}></i>
                        <span>{icon.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Button Text">
              <Input
                value={form.buttonText}
                onChange={(e) => setForm({ ...form, buttonText: e.target.value })}
                placeholder="Learn More"
                className="bg-background"
              />
            </Field>

            <Field label="Display Order">
              <Input
                type="number"
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value) || 0 })}
                className="bg-background"
              />
            </Field>

          </div>
          
          <DialogFooter className="pt-4 border-t border-border/50 mt-4">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={save} className="min-w-32 shadow-md hover:shadow-lg transition-all">
              {editing ? "Save changes" : "Add Organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete organization?"
        description="This will permanently remove the organization from your portfolio. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />
    </div>
  );
}

const Field = ({ label, span = 1, required = false, children }) => (
  <div className={span === 2 ? "col-span-1 md:col-span-2" : "col-span-1"}>
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1.5">
      {label}
      {required && <span className="text-destructive">*</span>}
    </Label>
    {children}
  </div>
);
